import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X, ChevronRight, Users, Tag, Wrench, CheckCircle, UserPlus, HelpCircle } from 'lucide-react';
import AssigneePicker from './AssigneePicker';
import NewContactForm from './NewContactForm';
import QuickAdd from '../QuickAdd';
import { ticketsApi } from '../../api/tickets';
import { categoriesApi } from '../../api/categories';
import { productLinesApi } from '../../api/productLines';
import { useClassifications } from '../../hooks/useClassifications';
import { fieldHelp } from '../../lib/fieldHelp';
import { teamsApi } from '../../api/teams';
import { companiesApi } from '../../api/companies';
import { crmContactsApi, crmOrgsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useTicketStatuses } from '../../hooks/useTicketStatuses';
import { addCreatedToCache, invalidateCrm } from '../../lib/queryCache';
import type {
  Ticket,
  TicketPriority,
  TicketType,
  TicketImpact,
  TicketEnvironment,
  SatisfactionRating,
} from '../../types';

// ─── Constants ────────────────────────────────────────────────────────────────

// Type / Priority / Impact / Environment are per-company pick-lists
// (Company Settings -> Classification), read through useClassifications.
// Satisfaction below is a fixed rating scale, not a tenant list.

const SATISFACTION_OPTIONS: { value: SatisfactionRating; label: string }[] = [
  { value: 'very_satisfied', label: 'Very Satisfied' },
  { value: 'satisfied', label: 'Satisfied' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'dissatisfied', label: 'Dissatisfied' },
  { value: 'very_dissatisfied', label: 'Very Dissatisfied' },
];

type TabId = 'basic' | 'assignment' | 'details' | 'technical' | 'resolution';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'basic',      label: 'Basic',      icon: <Tag className="w-3.5 h-3.5" /> },
  { id: 'assignment', label: 'Assignment', icon: <Users className="w-3.5 h-3.5" /> },
  { id: 'details',    label: 'Details',    icon: <ChevronRight className="w-3.5 h-3.5" /> },
  { id: 'technical',  label: 'Technical',  icon: <Wrench className="w-3.5 h-3.5" /> },
  { id: 'resolution', label: 'Resolution', icon: <CheckCircle className="w-3.5 h-3.5" /> },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Same hover explanations as the new-ticket form — one shared source. */
function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  const help = fieldHelp(label);
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5 uppercase tracking-wide" title={help}>
        {label}
        {help && <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />}
      </label>
      {children}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

interface TicketEditModalProps {
  ticket: Ticket;
  onClose: () => void;
  onSaved?: () => void;
}

export default function TicketEditModal({ ticket, onClose, onSaved }: TicketEditModalProps) {
  const queryClient = useQueryClient();
  const { currentCompany } = useCompany();
  const { isAdmin } = usePermissions();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [showNewContact, setShowNewContact] = useState(false);

  // ── Field state ──────────────────────────────────────────────────────────
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [status, setStatus] = useState(ticket.status);
  const [closeReason, setCloseReason] = useState('');
  const [priority, setPriority] = useState(ticket.priority);
  const [type, setType] = useState<TicketType>(ticket.type ?? 'incident');
  const [impact, setImpact] = useState<TicketImpact | ''>(ticket.impact ?? '');
  const [environment, setEnvironment] = useState<TicketEnvironment | ''>(ticket.environment ?? '');

  // Per-company pick-lists. Each is asked for the value the ticket currently
  // holds so a type that has since been turned off stays visible on the
  // tickets that carry it — editing anything else must not quietly reclassify
  // the ticket. The backend accepts an unchanged value for the same reason.
  const { optionsFor } = useClassifications();
  const typeOptions     = optionsFor('type', type);
  const priorityOptions = optionsFor('priority', priority);
  const impactOptions   = optionsFor('impact', impact);
  const envOptions      = optionsFor('environment', environment);
  const [product, setProduct] = useState(ticket.product ?? '');
  const [productVersion, setProductVersion] = useState(ticket.productVersion ?? '');
  const [department, setDepartment] = useState(ticket.department ?? '');
  const [location, setLocation] = useState(ticket.location ?? '');
  const [dueDate, setDueDate] = useState(ticket.dueDate ? ticket.dueDate.slice(0, 10) : '');
  const [estimatedHours, setEstimatedHours] = useState(ticket.estimatedHours?.toString() ?? '');
  const [browser, setBrowser] = useState(ticket.browser ?? '');
  const [device, setDevice] = useState(ticket.device ?? '');
  const [operatingSystem, setOperatingSystem] = useState(ticket.operatingSystem ?? '');
  const [reproducibility, setReproducibility] = useState(ticket.reproducibility ?? '');
  const [stepsToReproduce, setStepsToReproduce] = useState(ticket.stepsToReproduce ?? '');
  const [resolution, setResolution] = useState(ticket.resolution ?? '');
  const [rootCause, setRootCause] = useState(ticket.rootCause ?? '');
  const [satisfactionRating, setSatisfactionRating] = useState<SatisfactionRating | ''>(ticket.satisfactionRating ?? '');
  const [satisfactionComment, setSatisfactionComment] = useState(ticket.satisfactionComment ?? '');
  const [categoryId, setCategoryId] = useState(ticket.categoryId ?? '');
  const [productLineId, setProductLineId] = useState((ticket as any).productLineId ?? (ticket as any).productLine?.id ?? '');
  const [assignedTeamId, setAssignedTeamId] = useState((ticket.assignedTeam as any)?.id ?? '');
  const [contactId, setContactId] = useState(ticket.contactId ?? '');
  const [crmOrganizationId, setCrmOrganizationId] = useState((ticket as any).crmOrganizationId ?? '');
  const [crmContactId, setCrmContactId] = useState((ticket as any).crmContactId ?? '');
  const [crmContactName, setCrmContactName] = useState('');

  // primary + co-assignees
  const [assigneeIds, setAssigneeIds] = useState<string[]>(() => {
    const primary = (ticket.assignedTo as any)?.id ?? '';
    const co: string[] = ((ticket as any).coAssignees ?? []).map((ca: any) => ca.user?.id ?? ca.userId);
    return primary ? [primary, ...co.filter(id => id !== primary)] : co;
  });

  // Re-seed the form only when a DIFFERENT ticket is loaded into the modal.
  // Keying this on the `ticket` object meant any background refetch of the
  // ticket query — a socket update, a window refocus, an invalidate fired by
  // creating a CRM record from inside this modal — handed us a new object and
  // silently reset every field back to the server's values, throwing away
  // whatever was typed or picked. That looked exactly like a page refresh.
  useEffect(() => {
    setTitle(ticket.title);
    setDescription(ticket.description);
    setStatus(ticket.status);
    setCloseReason('');
    setPriority(ticket.priority);
    setType((ticket.type as TicketType) ?? 'incident');
    setImpact((ticket.impact as TicketImpact) ?? '');
    setEnvironment((ticket.environment as TicketEnvironment) ?? '');
    setProduct(ticket.product ?? '');
    setProductVersion(ticket.productVersion ?? '');
    setDepartment(ticket.department ?? '');
    setLocation(ticket.location ?? '');
    setDueDate(ticket.dueDate ? ticket.dueDate.slice(0, 10) : '');
    setEstimatedHours(ticket.estimatedHours?.toString() ?? '');
    setBrowser(ticket.browser ?? '');
    setDevice(ticket.device ?? '');
    setOperatingSystem(ticket.operatingSystem ?? '');
    setReproducibility(ticket.reproducibility ?? '');
    setStepsToReproduce(ticket.stepsToReproduce ?? '');
    setResolution(ticket.resolution ?? '');
    setRootCause(ticket.rootCause ?? '');
    setSatisfactionRating((ticket.satisfactionRating as SatisfactionRating) ?? '');
    setSatisfactionComment(ticket.satisfactionComment ?? '');
    setCategoryId(ticket.categoryId ?? '');
    setProductLineId((ticket as any).productLineId ?? (ticket as any).productLine?.id ?? '');
    setAssignedTeamId((ticket.assignedTeam as any)?.id ?? '');
    setContactId(ticket.contactId ?? '');
    setCrmOrganizationId((ticket as any).crmOrganizationId ?? '');
    setCrmContactId((ticket as any).crmContactId ?? '');
    const primary = (ticket.assignedTo as any)?.id ?? '';
    const co: string[] = ((ticket as any).coAssignees ?? []).map((ca: any) => ca.user?.id ?? ca.userId);
    setAssigneeIds(primary ? [primary, ...co.filter(id => id !== primary)] : co);
  }, [ticket.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
    enabled: !!currentCompany,
  });

  const { data: productLines = [] } = useQuery({
    queryKey: ['product-lines'],
    queryFn: () => productLinesApi.list(),
    enabled: !!currentCompany,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list(),
    enabled: !!currentCompany,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId),
    enabled: !!companyId,
  });

  const { data: orgsData } = useQuery({
    queryKey: ['crm-orgs-picker', companyId],
    queryFn: () => crmOrgsApi.list({ limit: 200 }),
    enabled: !!currentCompany,
  });
  const organizations = (orgsData?.organizations ?? []) as any[];

  // Contacts are narrowed to the chosen organization, same as the new-ticket
  // form — picking an org then a contact from another one makes no sense.
  const { data: crmContacts } = useQuery({
    queryKey: ['crm-contacts-all', companyId, crmOrganizationId || 'all'],
    queryFn: () => crmContactsApi.list({ limit: 500, ...(crmOrganizationId && { organizationId: crmOrganizationId }) }),
    enabled: !!currentCompany,
  });

  // Moving the ticket to another organization drops a contact that belonged to
  // the old one, rather than leaving a mismatched pair on the ticket.
  useEffect(() => {
    if (!crmContactId || !crmOrganizationId) return;
    const picked = (crmContacts?.contacts ?? []).find((c: any) => c.id === crmContactId);
    if (picked && picked.organizationId && picked.organizationId !== crmOrganizationId) {
      setCrmContactId('');
      setCrmContactName('');
    }
  }, [crmContactId, crmOrganizationId, crmContacts]);

  // Company's custom status set — status is editable right from the edit panel.
  const { statuses: statusDefs, categoryOf } = useTicketStatuses();
  const statusOptions = statusDefs.filter(s => s.isActive || s.key === ticket.status);
  const currentCategory = categoryOf(ticket.status);
  const nextCategory = categoryOf(status);
  const statusChanged = status !== ticket.status;
  // Closing an unresolved ticket needs a reason; resolving needs a resolution
  // (the field on the Resolution tab, or one already saved on the ticket).
  const needsCloseReason = statusChanged && nextCategory === 'closed' && currentCategory !== 'resolved';

  // Resolve crmContact name on load
  useEffect(() => {
    if (crmContactId && crmContacts?.contacts) {
      const c = crmContacts.contacts.find((c: any) => c.id === crmContactId);
      if (c) setCrmContactName(`${c.firstName} ${c.lastName ?? ''}`.trim());
    }
  }, [crmContactId, crmContacts]);

  const ticketId = (ticket as any).id ?? (ticket as any)._id;

  // Members of each team — for the hover tooltip and the "team" tag in the
  // assignee picker. Team members stay individually selectable (e.g. as the
  // primary assignee); the backend dedupes assignment emails.
  const teamMembersOf = (teamId: string) =>
    (((teams as any[]).find((t: any) => t.id === teamId)?.members ?? []) as any[]);
  const teamMemberNames = (teamId: string) =>
    teamMembersOf(teamId)
      .map((m: any) => m.user?.name ?? m.user?.email)
      .filter(Boolean) as string[];
  const selectedTeamMemberIds = new Set(
    teamMembersOf(assignedTeamId).map((m: any) => m.userId ?? m.user?.id),
  );

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => ticketsApi.update(ticketId, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      // Status/priority edits change the list, Home panels, and stat boxes.
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-stats'] });
      queryClient.invalidateQueries({ queryKey: ['home-my-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['home-aged-tickets'] });
      // The organization decides who is eligible to collaborate, so a move
      // has to refresh that list rather than leave the old org's people.
      queryClient.invalidateQueries({ queryKey: ['collaborator-candidates', ticketId] });
      toast.success('Ticket updated');
      onSaved?.();
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (statusChanged && nextCategory === 'resolved' && !resolution.trim()) {
      setActiveTab('resolution');
      toast.error('Add a resolution before marking this ticket resolved');
      return;
    }
    if (needsCloseReason && !closeReason.trim()) {
      setActiveTab('basic');
      toast.error('A reason is required when closing a ticket that was not resolved');
      return;
    }
    const [primaryId, ...coIds] = assigneeIds;
    updateMutation.mutate({
      title,
      description,
      ...(statusChanged && { status }),
      ...(statusChanged && needsCloseReason && { closedReason: closeReason.trim() }),
      priority,
      type,
      impact: impact || undefined,
      environment: environment || undefined,
      product: product || undefined,
      productVersion: productVersion || undefined,
      department: department || undefined,
      location: location || undefined,
      dueDate: dueDate || undefined,
      estimatedHours: estimatedHours ? parseFloat(estimatedHours) : undefined,
      browser: browser || undefined,
      device: device || undefined,
      operatingSystem: operatingSystem || undefined,
      reproducibility: reproducibility || undefined,
      stepsToReproduce: stepsToReproduce || undefined,
      resolution: resolution || undefined,
      rootCause: rootCause || undefined,
      satisfactionRating: satisfactionRating || undefined,
      satisfactionComment: satisfactionComment || undefined,
      categoryId: categoryId || undefined,
      // null (not undefined) so clearing the product line actually clears it.
      productLineId: productLineId || null,
      assignedToId: primaryId || undefined,
      coAssigneeIds: coIds,
      assignedTeamId: assignedTeamId || undefined,
      contactId: contactId || undefined,
      crmContactId: crmContactId || undefined,
      crmOrganizationId: crmOrganizationId || null,
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    // Backdrop
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      {/* Dim left side */}
      <div className="flex-1" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} />

      {/* Slide-over panel */}
      <div
        className="w-full max-w-xl h-full bg-surface shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line shrink-0">
          <div>
            <h2 className="text-base font-semibold text-fg">Edit Ticket</h2>
            <p className="text-xs text-muted mt-0.5 truncate max-w-xs">{ticket.title}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-muted hover:text-fg hover:bg-row-odd transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-line shrink-0 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-fg'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

            {/* ── Basic ─────────────────────────────────────────────────── */}
            {activeTab === 'basic' && (
              <>
                <FieldGroup label="Title">
                  <input
                    className="input w-full"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    required
                  />
                </FieldGroup>
                <FieldGroup label="Description">
                  <textarea
                    className="input w-full min-h-[120px] resize-y"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    required
                  />
                </FieldGroup>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Status">
                    <select className="select w-full" value={status} onChange={e => setStatus(e.target.value)}>
                      {statusOptions.map(s => (
                        <option key={s.key} value={s.key}>
                          {s.label}{!s.isActive ? ' (inactive)' : ''}
                        </option>
                      ))}
                    </select>
                    {statusChanged && nextCategory === 'resolved' && (
                      <p className="mt-1 text-[11px] text-muted">
                        Resolving — make sure the Resolution tab has a resolution entry.
                      </p>
                    )}
                  </FieldGroup>
                  {needsCloseReason && (
                    <FieldGroup label="Reason for closing *">
                      <input
                        className="input w-full"
                        value={closeReason}
                        onChange={e => setCloseReason(e.target.value)}
                        placeholder="e.g. duplicate, spam, no response…"
                      />
                    </FieldGroup>
                  )}
                  <FieldGroup label="Type">
                    <select className="select w-full" value={type} onChange={e => setType(e.target.value)}>
                      {typeOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Priority">
                    <select className="select w-full" value={priority} onChange={e => setPriority(e.target.value)}>
                      {priorityOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Category">
                    <select className="select w-full" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                      <option value="">None</option>
                      {(categories as any[]).map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {isAdmin && (
                      <QuickAdd
                        noun="category"
                        create={name => categoriesApi.create({ name })}
                        onCreated={c => {
                          addCreatedToCache(queryClient, { keyPrefix: 'categories', item: c });
                          setCategoryId(c.id);
                        }}
                      />
                    )}
                  </FieldGroup>
                  {(productLines as any[]).length > 0 && (
                    <FieldGroup label="Product Line">
                      <select className="select w-full" value={productLineId} onChange={e => setProductLineId(e.target.value)}>
                        <option value="">None</option>
                        {(productLines as any[]).map((pl: any) => (
                          <option key={pl.id} value={pl.id}>{pl.name}</option>
                        ))}
                      </select>
                    </FieldGroup>
                  )}
                </div>
              </>
            )}

            {/* ── Assignment ────────────────────────────────────────────── */}
            {activeTab === 'assignment' && (
              <>
                <FieldGroup label="Assignees (first = primary)">
                  <AssigneePicker
                    members={members as any[]}
                    selectedIds={assigneeIds}
                    onChange={setAssigneeIds}
                    teamMemberIds={selectedTeamMemberIds as Set<string>}
                  />
                </FieldGroup>

                <FieldGroup label="Team">
                  <select
                    className="select w-full"
                    value={assignedTeamId}
                    onChange={e => setAssignedTeamId(e.target.value)}
                    title={assignedTeamId ? `Members: ${teamMemberNames(assignedTeamId).join(', ') || 'none'}` : undefined}
                  >
                    <option value="">None</option>
                    {(teams as any[]).map((t: any) => (
                      <option key={t.id} value={t.id} title={`Members: ${teamMemberNames(t.id).join(', ') || 'none'}`}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {assignedTeamId && (
                    <p className="mt-1.5 text-xs text-muted">
                      Team members: {teamMemberNames(assignedTeamId).join(', ') || 'no members'}.
                      Everyone on the team is notified; picking one individually too (e.g. as primary) won't double-email them.
                    </p>
                  )}
                  {isAdmin && (
                    <QuickAdd
                      noun="team"
                      create={name => teamsApi.create({ name })}
                      onCreated={t => {
                        addCreatedToCache(queryClient, { keyPrefix: 'teams', item: t });
                        setAssignedTeamId(t.id);
                      }}
                    />
                  )}
                </FieldGroup>

                <FieldGroup label="On behalf of (staff member)">
                  <select className="select w-full" value={contactId} onChange={e => setContactId(e.target.value)}>
                    <option value="">Same as creator</option>
                    {(members as any[])
                      .filter((m: any) => ['owner', 'admin', 'technician'].includes(m.role))
                      .map((m: any) => (
                        <option key={m.user?.id} value={m.user?.id}>
                          {m.user?.name ? `${m.user.name} (${m.user.email})` : m.user?.email}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-[11px] text-muted">
                    Optional — a staff member this ticket was raised for. The customer-side person goes under Customer contact below.
                  </p>
                </FieldGroup>

                {/* Organization is editable after creation: it decides who can
                    be added as a collaborator, so a ticket filed against the
                    wrong one (or none) has to be movable. */}
                <FieldGroup label="Organization">
                  <select
                    className="select w-full"
                    value={crmOrganizationId}
                    onChange={e => setCrmOrganizationId(e.target.value)}
                  >
                    <option value="">None</option>
                    {organizations.map((o: any) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-muted">
                    Who the ticket is for. Collaborators can only be people from this organization.
                  </p>
                  {isAdmin && (
                    <QuickAdd
                      noun="organization"
                      create={name => crmOrgsApi.create({ name })}
                      onCreated={o => {
                        addCreatedToCache(queryClient, {
                          keyPrefix: 'crm-orgs',
                          listField: 'organizations',
                          item: o,
                        });
                        invalidateCrm(queryClient);
                        setCrmOrganizationId(o.id);
                      }}
                    />
                  )}
                </FieldGroup>

                <FieldGroup label="Customer contact (CRM)">
                  <div className="space-y-1">
                    <select
                      className="select w-full"
                      value={crmContactId}
                      onChange={e => {
                        setCrmContactId(e.target.value);
                        const c = crmContacts?.contacts?.find((c: any) => c.id === e.target.value);
                        setCrmContactName(c ? `${c.firstName} ${c.lastName ?? ''}`.trim() : '');
                        setShowNewContact(false);
                      }}
                    >
                      <option value="">None</option>
                      {(crmContacts?.contacts ?? []).map((c: any) => (
                        <option key={c.id} value={c.id}>{c.firstName} {c.lastName} {c.email ? `(${c.email})` : ''}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewContact(v => !v)}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Add new contact
                    </button>
                    {showNewContact && (
                      <NewContactForm
                        organizationId={crmOrganizationId || undefined}
                        onCreated={(contact) => {
                          addCreatedToCache(queryClient, {
                            keyPrefix: 'crm-contacts',
                            listField: 'contacts',
                            item: contact,
                          });
                          invalidateCrm(queryClient);
                          setCrmContactId(contact.id);
                          setCrmContactName(`${contact.firstName} ${contact.lastName ?? ''}`.trim());
                          setShowNewContact(false);
                        }}
                        onCancel={() => setShowNewContact(false)}
                      />
                    )}
                    {crmContactId && crmContactName && !showNewContact && (
                      <p className="text-xs text-muted">Selected: <span className="font-medium text-fg">{crmContactName}</span></p>
                    )}
                  </div>
                </FieldGroup>
              </>
            )}

            {/* ── Details ───────────────────────────────────────────────── */}
            {activeTab === 'details' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Impact">
                    <select className="select w-full" value={impact} onChange={e => setImpact(e.target.value)}>
                      <option value="">None</option>
                      {impactOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Environment">
                    <select className="select w-full" value={environment} onChange={e => setEnvironment(e.target.value)}>
                      <option value="">None</option>
                      {envOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Product">
                    <input className="input w-full" value={product} onChange={e => setProduct(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="Version">
                    <input className="input w-full" value={productVersion} onChange={e => setProductVersion(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="Department">
                    <input className="input w-full" value={department} onChange={e => setDepartment(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="Location">
                    <input className="input w-full" value={location} onChange={e => setLocation(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="Due date">
                    <input type="date" className="input w-full" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="Estimated hours">
                    <input type="number" step="0.5" min="0" className="input w-full" value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)} />
                  </FieldGroup>
                </div>
              </>
            )}

            {/* ── Technical ─────────────────────────────────────────────── */}
            {activeTab === 'technical' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FieldGroup label="Browser">
                    <input className="input w-full" value={browser} onChange={e => setBrowser(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="Device">
                    <input className="input w-full" value={device} onChange={e => setDevice(e.target.value)} />
                  </FieldGroup>
                  <FieldGroup label="OS">
                    <input className="input w-full" value={operatingSystem} onChange={e => setOperatingSystem(e.target.value)} />
                  </FieldGroup>
                </div>
                <FieldGroup label="Reproducibility">
                  <select className="select w-full" value={reproducibility} onChange={e => setReproducibility(e.target.value)}>
                    <option value="">None</option>
                    <option value="always">Always</option>
                    <option value="sometimes">Sometimes</option>
                    <option value="once">Once</option>
                    <option value="n_a">N/A</option>
                  </select>
                </FieldGroup>
                <FieldGroup label="Steps to reproduce">
                  <textarea className="input w-full min-h-[100px] resize-y font-mono text-xs" value={stepsToReproduce} onChange={e => setStepsToReproduce(e.target.value)} />
                </FieldGroup>
              </>
            )}

            {/* ── Resolution ────────────────────────────────────────────── */}
            {activeTab === 'resolution' && (
              <>
                <FieldGroup label="How was it resolved?">
                  <textarea className="input w-full min-h-[100px] resize-y" value={resolution} onChange={e => setResolution(e.target.value)} placeholder="Describe the resolution..." />
                </FieldGroup>
                <FieldGroup label="Root cause">
                  <textarea className="input w-full min-h-[80px] resize-y" value={rootCause} onChange={e => setRootCause(e.target.value)} placeholder="Root cause analysis..." />
                </FieldGroup>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Satisfaction rating">
                    <select className="select w-full" value={satisfactionRating} onChange={e => setSatisfactionRating(e.target.value as SatisfactionRating | '')}>
                      <option value="">Not rated</option>
                      {SATISFACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Satisfaction comment">
                    <input className="input w-full" value={satisfactionComment} onChange={e => setSatisfactionComment(e.target.value)} />
                  </FieldGroup>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 px-5 py-4 border-t border-line flex items-center justify-between bg-row-odd">
            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save changes'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
            <div className="flex gap-1">
              {TABS.map((tab, i) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-2 h-2 rounded-full transition-colors ${activeTab === tab.id ? 'bg-primary' : 'bg-line hover:bg-muted'}`}
                  title={tab.label}
                />
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
